import Stamp from "../../models/stamp";
import { useCallback, useEffect, useState } from "react";
import Errors, { getFieldErrors } from "../../models/errors";
import { createItem, getItem } from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import { errorAlert, warningAlert } from "../utils/messages";
import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CInputCheckbox,
  CLabel,
  CRow,
  CSelect,
} from "@coreui/react";
import { FieldErrors } from "../form/FieldErrors";
import { emptyValueOnUndefined } from "../../utils/fields";
import { Spinner } from "react-bootstrap";
import Loading from "../indicators/Loading";
import TaxPayer from "../../models/tax-payer";
import TaxPayerElectronicDocumentLimit, {
  LIMIT_PERIOD_CHOICES,
} from "../../models/tax-payer-electronic-document-limits";

interface TaxPayerLimitsFormProps {
  taxPayer?: TaxPayer;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
  clearOnSuccess?: boolean;
}

const TaxPayerLimitsForm: React.FC<TaxPayerLimitsFormProps> = ({
  taxPayer,
  onCancel,
  onSuccess,
  clearOnSuccess,
}) => {
  // TODO: verify error with regular clearOnSuccess bool
  // eslint-disable-next-line
  const [shouldClearOnSuccess, _] = useState(clearOnSuccess);

  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [limits, setLimits] = useState<TaxPayerElectronicDocumentLimit>({});

  const onLimitPeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLimits((prev) => {
      return { ...prev, limitPeriod: e.target.value };
    });
  };

  const onLimitDesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLimits((prev) => {
      let newLimit: number | undefined = undefined;
      if (!isNaN(parseInt(e.target.value))) {
        newLimit = parseInt(e.target.value);
      }
      return { ...prev, limitDes: newLimit };
    });
  };

  const onGraceAmountDesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLimits((prev) => {
      let newLimit: number | undefined = undefined;
      if (!isNaN(parseInt(e.target.value))) {
        newLimit = parseInt(e.target.value);
      }
      return { ...prev, graceAmountDes: newLimit };
    });
  };

  const onLimitReceiptsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLimits((prev) => {
      let newLimit: number | undefined = undefined;
      if (!isNaN(parseInt(e.target.value))) {
        newLimit = parseInt(e.target.value);
      }
      return { ...prev, limitReceipts: newLimit };
    });
  };

  const onGraceAmountReceiptsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLimits((prev) => {
      let newLimit: number | undefined = undefined;
      if (!isNaN(parseInt(e.target.value))) {
        newLimit = parseInt(e.target.value);
      }
      return { ...prev, graceAmountReceipts: newLimit };
    });
  };

  const onReceiptsUseDesCounterChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLimits((prev) => ({
      ...prev,
      receiptsUseDesCounter: e.target.checked,
    }));
  };

  const onSave = useCallback(async () => {
    if (taxPayer === undefined) {
      return;
    }
    const newLimits = {
      ...limits,
      taxPayerId: taxPayer.id,
      limitReceipts: limits.limitReceipts ?? 0,
      graceAmountReceipts: limits.graceAmountReceipts ?? 0,
    };

    const requestStatus = await createItem(
      `/tax_payers/${taxPayer.id}/limits/`,
      newLimits
    );

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
    } else {
      if (shouldClearOnSuccess) {
        setErrors({});
        clearForm();
      }

      onSuccess();
    }

    if (shouldClearOnSuccess) {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer, limits]);

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setLimits({});
  };

  const loadLimits = async (newTaxPayer?: Stamp) => {
    setLoading(true);
    if (newTaxPayer === undefined || newTaxPayer.id === undefined) {
      setLimits({});
      setLoading(false);
      return;
    }

    const requestStatus = await getItem<TaxPayerElectronicDocumentLimit>(
      `/tax_payers/${newTaxPayer.id}/limits/`
    );

    if (
      requestStatus.status !== SUCCESS &&
      requestStatus.status !== NOT_FOUND
    ) {
      let message = "Ha ocurrido un error";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }

      warningAlert(message);

      setLimits({});
      setLoading(false);
      return;
    }

    if (requestStatus.status === NOT_FOUND) {
      setLimits(requestStatus.data ?? {});
      setLoading(false);
      return;
    }

    if (requestStatus.data === undefined) {
      setLimits({});
      setLoading(false);
      return;
    }

    setLimits(requestStatus.data);
    setLoading(false);
  };

  useEffect(() => {
    loadLimits(taxPayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  if (loading) {
    return <Loading height="30vh" />;
  }

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Tipo:</CLabel>
            </CCol>
            <CCol>
              <CSelect
                type="text"
                value={emptyValueOnUndefined(limits.limitPeriod)}
                onChange={onLimitPeriodChange}
              >
                <option value={""} disabled>
                  -----
                </option>
                {Array.from(LIMIT_PERIOD_CHOICES.entries()).map((entry) => {
                  return (
                    <option key={entry[0]} value={entry[0]}>
                      {entry[1]}
                    </option>
                  );
                })}
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("type", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel htmlFor="receiptsUseDesCounter">
                Contador de recibos:
              </CLabel>
            </CCol>
            <CCol>
              <CInputCheckbox
                id="receiptsUseDesCounter"
                checked={limits.receiptsUseDesCounter === true}
                onChange={onReceiptsUseDesCounterChange}
              />
              <CLabel htmlFor="receiptsUseDesCounter" className="ml-2">
                Contabilizar recibos dentro del límite de DEs
              </CLabel>
              {limits.receiptsUseDesCounter === true && (
                <small className="text-muted d-block">
                  Cada recibo emitido incrementará el contador de DEs.
                </small>
              )}
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>
                <span className="text-danger">*</span> Límite Des:
              </CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                onChange={onLimitDesChange}
                placeholder="Límite Des"
                value={emptyValueOnUndefined(limits.limitDes)}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("limitDes", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>
                <span className="text-danger">*</span> Cant. de Gracia Des:
              </CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                onChange={onGraceAmountDesChange}
                placeholder="Cant. de Gracia Des"
                value={emptyValueOnUndefined(limits.graceAmountDes)}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("graceAmountDes", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>
                <span className="text-danger">*</span> Límite Recibos:
              </CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                onChange={onLimitReceiptsChange}
                placeholder="Límite Recibos"
                value={emptyValueOnUndefined(limits.limitReceipts)}
                disabled={limits.receiptsUseDesCounter === true}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("limitReceipts", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>
                <span className="text-danger">*</span> Cant. de Gracia Recibos:
              </CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                onChange={onGraceAmountReceiptsChange}
                placeholder="Cant. de Gracia Recibos"
                value={emptyValueOnUndefined(limits.graceAmountReceipts)}
                disabled={limits.receiptsUseDesCounter === true}
              ></CInput>
              <FieldErrors
                errors={
                  getFieldErrors("graceAmountReceipts", errors) as string[]
                }
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Salir
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default TaxPayerLimitsForm;
